<script setup>
	import { ref, computed, onMounted } from 'vue';
	import { usePage, useForm, router } from '@inertiajs/vue3';
	import { toast } from 'vue3-toastify';

	const page = usePage();

	const props = defineProps({
		contact: {
			type: Object,
			required: true,
			default: () => ({}),
		},
	});

	const emit = defineEmits(['toggle', 'close']);

	const formLoading = ref(false);
	const searchSkills = ref('');
	const searchCompetitors = ref('');
	const searchActivities = ref('');
	const searchDRs = ref('');

	const form = useForm({
		first_name: '',
		last_name: '',
		role: '',
		email: '',
		phone: '',
		secondary_email: '',
		secondary_phone: '',
		address: '',
		postal_code: '',
		city: '',
		pro_id: '',
		adeli: '',
		potential: '',
		targeted: null,
		objective: '',
		comment: '',
		schedule: '',
		skills: [],
		competitors: [],
		activities: [],
		status: null,
		target: null,
		service: null,
		dr: [],
		infos: {
			communication: [],
			visit: [],
		},
	});

	const getDRUserById = (userId) => {
		return page.props.admin.users.find((user) => user.id == userId);
	};

	const searchSkill = computed(() => {
		if (searchSkills.value) {
			return page.props.str.statuses.filter((w) => w.type === 'skill' && w.name.toLowerCase().includes(searchSkills.value.toLowerCase()));
		}
		return page.props.str.statuses.filter((w) => w.type === 'skill');
	});

	const searchCompetitor = computed(() => {
		if (searchCompetitors.value) {
			return page.props.str.statuses.filter(
				(w) => w.type === 'competitor' && w.name.toLowerCase().includes(searchCompetitors.value.toLowerCase()),
			);
		}
		return page.props.str.statuses.filter((w) => w.type === 'competitor');
	});

	const searchActivity = computed(() => {
		if (searchActivities.value) {
			return page.props.str.statuses.filter(
				(w) => w.type === 'activity' && w.name.toLowerCase().includes(searchActivities.value.toLowerCase()),
			);
		}
		return page.props.str.statuses.filter((w) => w.type === 'activity');
	});

	const searchDR = computed(() => {
		if (searchDRs.value) {
			return page.props.admin.users.filter(
				(w) =>
					w.first_name.toLowerCase().includes(searchDRs.value.toLowerCase()) ||
					w.last_name.toLowerCase().includes(searchDRs.value.toLowerCase()),
			);
		}
		return page.props.admin.users;
	});

	const doUpdate = () => {
		toast.info('Contact en cours de modification');
		formLoading.value = true;
		form.post(route('contacts.update', props.contact.id), {
			preserveScroll: true,
			onSuccess: () => {
				formLoading.value = false;
				toast.success('Contact modifié avec succès !');
				getContactInfo();
			},
			onFinish: () => {
				emit('toggle');
				formLoading.value = false;
			},
		});
	};

	defineExpose({
		doUpdate,
	});
	const skillAdd = (skill) => {
		if (form.skills.includes(skill.id)) {
			toast.warn('La profession est déjà présente dans la liste');
		} else {
			form.skills.push(skill.id);
		}
	};

	const skillDelete = (skill) => {
		form.skills = form.skills.filter((element) => element !== skill);
	};

	const activityAdd = (activity) => {
		if (form.activities.includes(activity.id)) {
			toast.warn("L'activité est déjà présente dans la liste");
		} else {
			form.activities.push(activity.id);
		}
	};

	const activityDelete = (activity) => {
		form.activities = form.activities.filter((element) => element !== activity);
	};

	const competitorAdd = (competitor) => {
		if (form.competitors.includes(competitor.id)) {
			toast.warn('Le concurrent est déjà présent dans la liste');
		} else {
			form.competitors.push(competitor.id);
		}
	};

	const competitorDelete = (competitor) => {
		form.competitors = form.competitors.filter((element) => element !== competitor);
	};

	const addDR = (DR) => {
		if (form.dr.includes(DR.id)) {
			toast.error('Le DR est déjà présent dans la liste');
		} else {
			form.dr.push(DR.id);
		}
	};

	const deleteDR = (DR) => {
		form.dr = form.dr.filter((element) => element != DR);
	};

	const getContactInfo = () => {
		form.first_name = props.contact.first_name;
		form.last_name = props.contact.last_name;
		form.role = props.contact.role;
		form.email = props.contact.email;
		form.phone = props.contact.phone;
		form.secondary_email = props.contact.secondary_email;
		form.secondary_phone = props.contact.secondary_phone;
		form.address = props.contact.address;
		form.postal_code = props.contact.postal_code;
		form.city = props.contact.city;
		form.pro_id = props.contact.pro_id;
		form.adeli = props.contact.adeli;
		form.potential = props.contact.potential;
		form.targeted = props.contact.targeted;
		form.objective = props.contact.objective;
		form.comment = props.contact.comment;
		form.schedule = props.contact.schedule;
		form.skills = props.contact.skills?.map((skill) => skill.id) || [];
		form.competitors = props.contact.competitors?.map((competitor) => competitor.id) || [];
		form.activities = props.contact.activities?.map((activity) => activity.id) || [];
		form.status = props.contact.status?.id || null;
		form.target = props.contact.target?.id || null;
		form.service = props.contact.service || null;
		form.dr = [...new Set(props.contact.dr?.map((dr) => dr.id))] || [];
		form.infos = props.contact.infos || { communication: [], visit: null };
	};

	onMounted(() => {
		getContactInfo();
	});
</script>

<template>
	<form @submit.prevent="doUpdate()" @keyup.enter="doUpdate()" class="flex flex-col lg:grid lg:grid-cols-2 lg:col-span-2 gap-3 w-full">
		<!-- TARGETED objective -->
		<span class="flex justify-between gap-2 items-center col-span-2">
			Cibler le contact ? :
			<ui-toggle v-model="form.targeted" :icons="['fa-crosshairs', 'fa-crosshairs']" />
		</span>
		<!-- CIBLE -->
		<form-select
			:items="page.props.str.statuses.filter((status) => status.type == 'target')"
			placeholder="Cible"
			@valueChange="form.target = $event"
			:value="form.target"
			class="col-span-2">
		</form-select>
		<!-- STATUS -->
		<form-select
			:items="page.props.str.statuses.filter((status) => status.type == 'progress')"
			placeholder="Status"
			@valueChange="form.status = $event"
			:value="form.status"
			class="col-span-2">
		</form-select>
		<!-- NAME + SURNAME -->
		<form-input v-model="form.first_name" placeholder="Prénom"> </form-input>
		<form-input v-model="form.last_name" placeholder="Nom"> </form-input>
		<!-- ADDRESS -->
		<form-input placeholder="Adresse" v-model="form.address" class="lg:col-span-2"> </form-input>
		<!-- CITY + POSTAL CODE -->
		<form-input v-model="form.postal_code" placeholder="Code Postal"> </form-input>
		<form-input v-model="form.city" placeholder="Ville"> </form-input>
		<!-- PHONE + EMAIL -->
		<form-input v-model="form.phone" placeholder="Téléphone"> </form-input>
		<form-input v-model="form.email" placeholder="E-Mail"> </form-input>
		<form-input v-model="form.secondary_phone" placeholder="Téléphone secondaire"> </form-input>
		<form-input v-model="form.secondary_email" placeholder="E-Mail secondaire"> </form-input>
		<!-- PRO_ID -->
		<form-input placeholder="RPPS" v-model="form.pro_id" class="lg:col-span-2"> </form-input>
		<form-input placeholder="ADELI" v-model="form.adeli" class="lg:col-span-2"> </form-input>
		<h2 class="lg:col-span-2 font-medium">Informations complémentaires</h2>
		<!-- POTENTIAL -->
		<form-input placeholder="Potentiel d'installation mensuel" v-model="form.potential" class="lg:col-span-2"> </form-input>
		<!-- POTENTIAL -->
		<form-input placeholder="Objectif de visite quadrimestriel" v-model="form.objective" class="lg:col-span-2"> </form-input>
		<!-- COMMENT -->
		<form-textarea :placeholder="'Commentaire'" v-model="form.comment" class="col-span-2"> </form-textarea>
		<!-- SCHEDULE -->
		<form-textarea :placeholder="'Horaires'" v-model="form.schedule" class="col-span-2"> </form-textarea>
		<!-- DR -->
		<h3 v-if="page.props.auth.user.role == 'admin'" class="text-lg border-b-2 mt-2 col-span-2">DR(s)</h3>
		<!-- DROPDOWN ADD DR-->
		<ui-dropdown ref="dropdownTest" class="col-span-2">
			<template #content>
				<form-input placeholder="Recherche" v-model="searchDRs" class="w-full border-none"> </form-input>
				<div
					v-for="DR in searchDR"
					:key="DR.id"
					class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer flex gap-2 justify-center"
					@click="addDR(DR)"
					data-trigger="close">
					{{ DR.first_name }}
					{{ DR.last_name }}
				</div>
			</template>
			<template v-slot:trigger>
				<ui-button class="w-full">
					<template v-slot:icon>
						<i class="fas fa-plus-circle"></i>
					</template>
					<template v-slot:text>Ajouter</template>
				</ui-button>
			</template>
		</ui-dropdown>

		<!-- ACTIVITY LIST DR-->
		<div class="flex flex-col gap-3 justify-between col-span-2">
			<div class="flex flex-wrap gap-1">
				<div
					v-if="form.dr"
					v-for="DR in form.dr"
					:key="DR"
					class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group text-xs"
					@click.prevent="deleteDR(DR)">
					<p v-if="getDRUserById(DR)">
						{{ getDRUserById(DR).first_name[0] + '.' }}
						{{ getDRUserById(DR).last_name }}
					</p>
				</div>
			</div>
		</div>
		<!-- CONTACT PREFERENCE -->
		<form-checkbox
			:items="page.props.str.statuses.filter((status) => status.type == 'communication')"
			v-model="form.infos.communication"
			label="Mode de communication"
			class="col-span-2">
		</form-checkbox>
		<!-- VISIT PREFERENCE -->
		<form-radio
			v-if="page.props.str.statuses.filter((status) => status.type == 'visit').length > 0"
			:items="page.props.str.statuses.filter((status) => status.type == 'visit')"
			v-model="form.infos.visit"
			label="Type de visites"
			class="col-span-2">
		</form-radio>
		<!-- SKILLS -->
		<h3 class="text-lg border-b-2 mt-2 col-span-2">Profession(s)</h3>
		<!-- DROPDOWN ADD SKILL -->
		<ui-dropdown ref="dropdownTest" class="col-span-2">
			<template #content>
				<form-input placeholder="Recherche" v-model="searchSkills" class="w-full border-none"> </form-input>
				<div
					v-for="skill in searchSkill"
					:key="skill.id"
					class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer"
					@click="skillAdd(skill)"
					data-trigger="close">
					{{ skill.name }}
				</div>
			</template>
			<template v-slot:trigger>
				<ui-button class="w-full">
					<template v-slot:icon>
						<i class="fas fa-plus-circle"></i>
					</template>
					<template v-slot:text>Ajouter</template>
				</ui-button>
			</template>
		</ui-dropdown>
		<!-- SKILL LIST -->
		<div class="flex flex-col gap-3 justify-between col-span-2">
			<div class="flex flex-wrap gap-1" v-if="form.skills">
				<div
					v-for="skill in form.skills"
					:key="skill"
					class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group"
					@click.prevent="skillDelete(skill)">
					{{ page.props.str.statuses.find((element) => element.id == skill)?.name }}
					<div class="absolute top-0 left-0 bg-red-500 w-full h-full hidden group-hover:flex items-center justify-center text-white">
						<i class="fas fa-trash"></i>
					</div>
				</div>
			</div>
		</div>
		<!-- COMPETITION -->
		<h3 class="text-lg border-b-2 mt-2 col-span-2">Concurrent(s)</h3>
		<!-- DROPDOWN ADD COMPETITION -->
		<ui-dropdown :closeOnClick="false" ref="dropdownTest" class="col-span-2">
			<template v-slot:content>
				<form-input placeholder="Recherche" v-model="searchCompetitors" class="w-full border-none"> </form-input>
				<div
					v-for="competitor in searchCompetitor"
					:key="competitor.id"
					class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer"
					@click="competitorAdd(competitor)"
					data-trigger="close">
					{{ competitor.name }}
				</div>
			</template>
			<template v-slot:trigger>
				<ui-button class="w-full">
					<template v-slot:icon>
						<i class="fas fa-plus-circle"></i>
					</template>
					<template v-slot:text>Ajouter</template>
				</ui-button>
			</template>
		</ui-dropdown>
		<!-- COMPETITION LIST-->
		<div class="flex flex-col gap-3 justify-between col-span-2">
			<div class="flex flex-wrap gap-1" v-if="form.competitors">
				<div
					v-for="competitor in form.competitors"
					:key="competitor"
					class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group"
					@click.prevent="competitorDelete(competitor)">
					{{ page.props.str.statuses.find((element) => element.id == competitor)?.name }}
					<div class="absolute top-0 left-0 bg-red-500 w-full h-full hidden group-hover:flex items-center justify-center text-white">
						<i class="fas fa-trash"></i>
					</div>
				</div>
			</div>
		</div>
		<!-- ACTIVITY -->
		<h3 class="text-lg border-b-2 mt-2 col-span-2">Activité(s)</h3>
		<!-- DROPDOWN ADD ACTIVITY -->
		<ui-dropdown :closeOnClick="false" ref="dropdownTest" class="col-span-2">
			<template v-slot:content>
				<form-input placeholder="Recherche" v-model="searchActivities" class="w-full border-none"> </form-input>
				<div
					v-for="activity in searchActivity"
					:key="activity.id"
					class="p-3 text-center hover:bg-slate-500 hover:text-white cursor-pointer"
					@click="activityAdd(activity)"
					data-trigger="close">
					{{ activity.name }}
				</div>
			</template>
			<template v-slot:trigger>
				<ui-button class="w-full">
					<template v-slot:icon>
						<i class="fas fa-plus-circle"></i>
					</template>
					<template v-slot:text>Ajouter</template>
				</ui-button>
			</template>
		</ui-dropdown>
		<!-- ACTIVITY LIST -->
		<div class="flex flex-col gap-3 justify-between col-span-2">
			<div class="flex flex-wrap gap-1" v-if="form.activities">
				<div
					v-for="activity in form.activities"
					:key="activity"
					class="text-slate-500 bg-slate-50 border border-slate-800 rounded-md p-2 px-5 whitespace-nowrap relative overflow-hidden cursor-pointer hover:border-red-400 group"
					@click.prevent="activityDelete(activity)">
					{{ page.props.str.statuses.find((element) => element.id == activity)?.name }}
					<div class="absolute top-0 left-0 bg-red-500 w-full h-full hidden group-hover:flex items-center justify-center text-white">
						<i class="fas fa-trash"></i>
					</div>
				</div>
			</div>
		</div>
		<ui-button @click="doUpdate" :loading="formLoading" class="col-span-2 mt-3 font-bold border-2">
			<template v-slot:icon><i class="fas fa-edit"></i></template>
			<template v-slot:text>Valider les modifications</template>
		</ui-button>
	</form>
</template>
